"use strict";

import { shopConstants, campaignConstants } from "../_constants";
import { ajaxError } from "../_actions";
import {
    isMarketplaceUnderMaintenance,
    MARKETPLACE_MAINTENANCE_MESSAGE,
} from "../../_helpers/marketplace.maintenance";

/**
 * Stops every action that would reach the voucher provider while its platform
 * is down, before the epic middleware turns it into a request.
 *
 * Two kinds of action are handled differently. A screen fetching its own data
 * is answered with an empty COMPLETE, so the section settles into its empty
 * state instead of waiting on a response that never arrives. Something the
 * member clicked is answered with the usual error banner, so they are told why
 * nothing happened.
 *
 * The points-balance and rebate actions are left alone: they go to the points
 * bank, not the voucher provider.
 */

// Payload shapes match what each reducer reads, so an empty COMPLETE leaves the
// slice in the same shape a real response would.
const EMPTY_COMPLETE_ACTIONS = {
    [shopConstants.TOP_PICKS_REQUEST]: {
        type: shopConstants.TOP_PICKS_COMPLETE,
        payload: { data: {} },
    },
    [shopConstants.TRENDINGS_REQUEST]: {
        type: shopConstants.TRENDINGS_COMPLETE,
        payload: { data: {} },
    },
    [shopConstants.FAVOURITES_REQUEST]: {
        type: shopConstants.FAVOURITES_COMPLETE,
        payload: { data: {} },
    },
    [shopConstants.VOUCHER_DETAILS_REQUEST]: {
        type: shopConstants.VOUCHER_DETAILS_COMPLETE,
        payload: { data: {} },
    },
    [shopConstants.ORDER_DETAILS_REQUEST]: {
        type: shopConstants.ORDER_DETAILS_COMPLETE,
        payload: { data: {} },
    },
    [shopConstants.VOUCHER_WALLET_REQUEST]: {
        type: shopConstants.VOUCHER_WALLET_COMPLETE,
        payload: { data: [] },
    },
    [shopConstants.BROWSE_PRODUCTS_REQUEST]: {
        type: shopConstants.BROWSE_PRODUCTS_COMPLETE,
        payload: { data: {} },
    },
    [shopConstants.BROWSE_PRODUCTS_POPULAR_FILTER_REQUEST]: {
        type: shopConstants.BROWSE_PRODUCTS_POPULAR_FILTER_COMPLETE,
        payload: { data: [] },
    },
    [shopConstants.BROWSE_PRODUCTS_CATEGORY_REQUEST]: {
        type: shopConstants.BROWSE_PRODUCTS_CATEGORY_COMPLETE,
        payload: { data: [] },
    },
    [campaignConstants.GET_HOME_BEST_VOUCHER_DEALS_TODAY_REQUEST]: {
        type: campaignConstants.GET_HOME_BEST_VOUCHER_DEALS_TODAY_COMPLETE,
        payload: { data: {} },
    },
    [campaignConstants.GET_HOME_TRENDING_VOUCHER_REQUEST]: {
        type: campaignConstants.GET_HOME_TRENDING_VOUCHER_COMPLETE,
        payload: { data: {} },
    },
};

const NOTIFY_ACTIONS = [
    shopConstants.CONFIRM_ORDER_REQUEST,
    shopConstants.REDEEM_VOUCHER_REQUEST,
    shopConstants.RATE_VOUCHER_REQUEST,
    shopConstants.BUY_POINTS_REQUEST,
    campaignConstants.CAMPAIGN_VOUCHER_ADDTO_FAVOURITE_REQUEST,
    campaignConstants.CAMPAIGN_VOUCHER_REMOVEFROM_FAVOURITE_REQUEST,
];

const marketplaceMaintenanceMiddleware = (store) => (next) => (action) => {
    if (
        typeof action === "undefined" ||
        typeof action.type === "undefined" ||
        !isMarketplaceUnderMaintenance()
    ) {
        return next(action);
    }

    const emptyComplete = EMPTY_COMPLETE_ACTIONS[action.type];
    if (emptyComplete) {
        return next(emptyComplete);
    }

    if (NOTIFY_ACTIONS.includes(action.type)) {
        // The same synthetic error the epics raise for a refused purchase, so
        // the member sees the banner the rest of the shop already uses.
        store.dispatch(
            ajaxError({
                response: {
                    message: MARKETPLACE_MAINTENANCE_MESSAGE,
                    statusCode: "333",
                },
            }),
        );
        return;
    }

    return next(action);
};

export { marketplaceMaintenanceMiddleware };
